import { useState, useMemo } from 'react';
import { Race, RaceAsset } from '../contexts/BettingProvider';

// Type definitions
export interface SelectedRaceBet {
  raceId: string;
  raceName: string;
  selectedAssets: string[];
  assetLogo: string;
  competitors: string[];
  placementType: 'single' | 'exacta' | 'trifecta';
  combinedOdds: number;
}

export interface ActiveBet {
  betId: string;
  betAmount: number;
  raceSelections: {
    raceId: string;
    selectedAssets: string[];
    betType: string;
    odds: number;
  }[];
  parlayMultiplier: number;
  potentialWin: number;
  placedAt: string;
  roundId: string;
  status: 'active' | 'won' | 'lost' | 'claimed';
  result?: 'win' | 'loss' | null; // Result from database (win/loss/null)
}

/**
 * Custom hook to manage betting state
 * 
 * Handles:
 * - Bet selections (single, exacta, trifecta)
 * - Bet amount
 * - Potential win calculations
 * - Payout validation
 * 
 * @param races - Array of available races
 * @param currentRound - Current round information
 * @param isBettingPhase - Whether betting is currently allowed
 * @returns Betting state and handlers
 */
export const useBettingState = (
  races: Race[],
  currentRound: any,
  isBettingPhase: boolean
) => {
  // Bet selection state
  const [selectedRaceBets, setSelectedRaceBets] = useState<SelectedRaceBet[]>([]);
  const [betAmount, setBetAmount] = useState<number>(0.01);

  // Calculate potential win for race bets (including placement bets)
  const potentialWin = useMemo(() => {
    if (selectedRaceBets.length === 0) return 0;
    const combinedMultiplier = selectedRaceBets.reduce((acc, bet) => {
      return acc * bet.combinedOdds;
    }, 1);
    return betAmount * combinedMultiplier;
  }, [selectedRaceBets, betAmount]);

  // Check if potential payout exceeds maximum allowed (1.0 SOL)
  const MAX_PAYOUT_SOL = 1.0;
  const exceedsMaxPayout = potentialWin > MAX_PAYOUT_SOL;
  const maxMultiplierForBet = betAmount > 0 ? MAX_PAYOUT_SOL / betAmount : 0;

  // Validation logic for bet placement
  const canPlaceBet = useMemo(() => {
    // Check if player has already picked an asset for this round
    const hasBetInCurrentRound = !!currentRound?.player_pick;
    
    return !hasBetInCurrentRound && isBettingPhase;
  }, [currentRound, isBettingPhase]);

  // Helper: Calculate placement odds
  const calculatePlacementOdds = (assets: string[], race: Race): number => {
    let combinedOdds = 1;
    for (const assetSymbol of assets) {
      const asset = race.assets.find(a => a.symbol === assetSymbol);
      if (asset) {
        combinedOdds *= asset.odds;
      }
    }
    // Cap at 100x
    return Math.min(combinedOdds, 100);
  };

  // Helper: Get placement type based on asset count
  const getPlacementType = (assetCount: number): 'single' | 'exacta' | 'trifecta' => {
    if (assetCount === 1) return 'single';
    if (assetCount === 2) return 'exacta';
    return 'trifecta';
  };

  // Handler: Select/deselect asset for betting
  const handleRaceBetSelect = (raceId: string, asset: RaceAsset) => {
    const race = races.find(r => r.id === raceId);
    if (!race) return;

    // Check if user already has a bet for this race
    const existingBetIndex = selectedRaceBets.findIndex(bet => bet.raceId === raceId);
    
    if (existingBetIndex >= 0) {
      const existingBet = selectedRaceBets[existingBetIndex];
      const currentAssets = existingBet.selectedAssets;
      
      // PINK SLIPS: 1v1 ONLY - clicking same asset removes bet
      if (currentAssets.includes(asset.symbol)) {
        // Remove bet entirely
        setSelectedRaceBets(prev => prev.filter((_, index) => index !== existingBetIndex));
        console.log('🏁 Removed race bet:', { raceId, asset: asset.symbol });
      } else {
        // PINK SLIPS: Replace with new selection (toggle behavior)
        const newBet: SelectedRaceBet = {
          ...existingBet,
          selectedAssets: [asset.symbol],
          placementType: 'single',
          combinedOdds: asset.odds
        };
        setSelectedRaceBets(prev => prev.map((bet, index) => 
          index === existingBetIndex ? newBet : bet
        ));
        console.log('🔄 Switched 1v1 selection:', { raceId, newAsset: asset.symbol, odds: asset.odds });
      }
    } else {
      // Create new single bet for this race
      const newBet: SelectedRaceBet = {
        raceId: race.id,
        raceName: race.name || `${race.assets[0].symbol} vs ${race.assets[1].symbol}`,
        selectedAssets: [asset.symbol],
        assetLogo: asset.logo || asset.symbol.substring(0, 1),
        competitors: race.assets.map(a => a.symbol),
        placementType: 'single',
        combinedOdds: asset.odds
      };
      setSelectedRaceBets(prev => [...prev, newBet]);
      console.log('🎯 Started new placement bet:', { raceId, asset: asset.symbol, odds: asset.odds });
    }
  };

  // Handler: Clear all bets
  const clearAllRaceBets = () => {
    setSelectedRaceBets([]);
  };

  return {
    // State
    selectedRaceBets,
    betAmount,
    potentialWin,
    exceedsMaxPayout,
    maxMultiplierForBet,
    canPlaceBet,
    MAX_PAYOUT_SOL,
    
    // Setters
    setBetAmount,
    setSelectedRaceBets,
    
    // Handlers
    handleRaceBetSelect,
    clearAllRaceBets
  };
};

